import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  alpha,
  styled,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SpeedIcon from '@mui/icons-material/Speed';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import SaveIcon from '@mui/icons-material/Save';
import { useOrganizationLimits, useUpdateOrganizationLimits, UpdateLimitsDto } from '../../hooks/subscriptions';

interface OrganizationLimitsTabProps {
  organizationId: string;
  organizationName: string;
}

const LimitCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: '12px',
  padding: '20px',
  height: '100%',
  border: '1px solid #e3e8ef',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    borderColor: '#667eea',
  },
}));

interface LimitItemProps {
  icon: React.ReactNode;
  label: string;
  current: number;
  limit: number;
  color: string;
}

const LimitItem = ({ icon, label, current, limit, color }: LimitItemProps) => {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && current >= limit;

  return (
    <LimitCard>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box flex={1}>
          <Typography
            variant="caption"
            sx={{
              color: '#697386',
              fontSize: '12px',
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            {label}
          </Typography>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography
              variant="h5"
              sx={{
                color: isAtLimit ? '#f44336' : '#1a1f36',
                fontWeight: 700,
                fontSize: '24px',
              }}
            >
              {current}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#697386', fontSize: '14px' }}
            >
              /
            </Typography>
            {isUnlimited ? (
              <Box display="flex" alignItems="center" gap={0.5}>
                <AllInclusiveIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                  Unlimited
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: '#697386', fontWeight: 600, fontSize: '16px' }}
              >
                {limit}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {!isUnlimited && (
        <Box>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e3e8ef',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: isAtLimit
                  ? '#f44336'
                  : isNearLimit
                    ? '#FF9800'
                    : `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              color: isAtLimit ? '#f44336' : isNearLimit ? '#FF9800' : '#697386',
              fontWeight: 600,
            }}
          >
            {percentage.toFixed(0)}% used
          </Typography>
        </Box>
      )}
    </LimitCard>
  );
};

export const OrganizationLimitsTab = ({
  organizationId,
  organizationName,
}: OrganizationLimitsTabProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<UpdateLimitsDto>({});

  const { data: limitsData, isLoading, error } = useOrganizationLimits(organizationId);
  const updateMutation = useUpdateOrganizationLimits(organizationId);

  const handleEditClick = () => {
    if (limitsData) {
      setEditForm({
        includedStores: limitsData.limits.includedStores,
        includedWarehouses: limitsData.limits.includedWarehouses,
        includedStaff: limitsData.limits.includedStaff,
        maxProducts: limitsData.limits.maxProducts,
        maxClients: limitsData.limits.maxClients,
        maxLocations: limitsData.limits.maxLocations,
        salesRateLimit: limitsData.limits.salesRateLimit,
        offlineTransactionLimit: limitsData.limits.offlineTransactionLimit,
        isVirtualOnly: limitsData.isVirtualOnly,
        hasStorefrontAccess: limitsData.hasStorefrontAccess,
        hasAnalyticsAccess: limitsData.hasAnalyticsAccess,
      });
      setEditDialogOpen(true);
    }
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(editForm);
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Failed to update limits:', err);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        Failed to load resource limits
      </Alert>
    );
  }

  if (!limitsData) {
    return (
      <Alert severity="info" sx={{ borderRadius: '12px' }}>
        No limits data available
      </Alert>
    );
  }

  const { limits, tier, isVirtualOnly, hasStorefrontAccess, hasAnalyticsAccess } = limitsData;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1f36' }}>
            Resource Limits for {organizationName}
          </Typography>
          <Box display="flex" gap={1} mt={1}>
            <Chip
              label={tier.toUpperCase()}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontWeight: 700,
              }}
            />
            {isVirtualOnly && (
              <Chip
                label="Virtual Only"
                size="small"
                sx={{ backgroundColor: '#FF9800', color: '#fff' }}
              />
            )}
            {hasStorefrontAccess && (
              <Chip
                label="Storefront"
                size="small"
                sx={{ backgroundColor: '#4CAF50', color: '#fff' }}
              />
            )}
            {hasAnalyticsAccess && (
              <Chip
                label="Analytics"
                size="small"
                sx={{ backgroundColor: '#2196F3', color: '#fff' }}
              />
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            },
          }}
        >
          Edit Limits
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <LimitItem
            icon={<StorefrontIcon sx={{ color: '#fff', fontSize: 24 }} />}
            label="Stores"
            current={limits.currentStores}
            limit={limits.includedStores}
            color="#667eea"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <LimitItem
            icon={<WarehouseIcon sx={{ color: '#fff', fontSize: 24 }} />}
            label="Warehouses"
            current={limits.currentWarehouses}
            limit={limits.includedWarehouses}
            color="#764ba2"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <LimitItem
            icon={<PeopleIcon sx={{ color: '#fff', fontSize: 24 }} />}
            label="Staff Users"
            current={limits.currentStaff}
            limit={limits.includedStaff}
            color="#4CAF50"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <LimitItem
            icon={<InventoryIcon sx={{ color: '#fff', fontSize: 24 }} />}
            label="Products"
            current={limits.currentProducts}
            limit={limits.maxProducts}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <LimitItem
            icon={<PersonIcon sx={{ color: '#fff', fontSize: 24 }} />}
            label="Clients"
            current={limits.currentClients}
            limit={limits.maxClients}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <LimitItem
            icon={<LocationOnIcon sx={{ color: '#fff', fontSize: 24 }} />}
            label="Total Locations"
            current={limits.currentLocations}
            limit={limits.maxLocations}
            color="#00BCD4"
          />
        </Grid>
      </Grid>

      <Box mt={4}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#1a1f36' }}>
          Rate Limits
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LimitCard>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SpeedIcon sx={{ color: '#fff', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#697386',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                    }}
                  >
                    Sales Rate Limit
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1a1f36', fontWeight: 700 }}>
                    {limits.salesRateLimit === -1 ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AllInclusiveIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                        <span style={{ color: '#4CAF50' }}>Unlimited</span>
                      </Box>
                    ) : (
                      `${limits.salesRateLimit} / hour`
                    )}
                  </Typography>
                </Box>
              </Box>
            </LimitCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <LimitCard>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #795548 0%, #5D4037 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SpeedIcon sx={{ color: '#fff', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#697386',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                    }}
                  >
                    Offline Transaction Limit
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1a1f36', fontWeight: 700 }}>
                    {limits.offlineTransactionLimit === -1 ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AllInclusiveIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                        <span style={{ color: '#4CAF50' }}>Unlimited</span>
                      </Box>
                    ) : (
                      limits.offlineTransactionLimit
                    )}
                  </Typography>
                </Box>
              </Box>
            </LimitCard>
          </Grid>
        </Grid>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit Resource Limits
          <Typography variant="body2" color="text.secondary">
            Set -1 for unlimited. Changes take effect immediately.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Location Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Stores"
                  type="number"
                  fullWidth
                  value={editForm.includedStores ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, includedStores: Number(e.target.value) })}
                  helperText="-1 = unlimited"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Warehouses"
                  type="number"
                  fullWidth
                  value={editForm.includedWarehouses ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, includedWarehouses: Number(e.target.value) })}
                  helperText="-1 = unlimited"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Max Locations"
                  type="number"
                  fullWidth
                  value={editForm.maxLocations ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, maxLocations: Number(e.target.value) })}
                  helperText="-1 = unlimited"
                  size="small"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              User & Resource Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Staff Users"
                  type="number"
                  fullWidth
                  value={editForm.includedStaff ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, includedStaff: Number(e.target.value) })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Max Products"
                  type="number"
                  fullWidth
                  value={editForm.maxProducts ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, maxProducts: Number(e.target.value) })}
                  helperText="-1 = unlimited"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Max Clients"
                  type="number"
                  fullWidth
                  value={editForm.maxClients ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, maxClients: Number(e.target.value) })}
                  helperText="-1 = unlimited"
                  size="small"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Rate Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Sales Rate Limit (per hour)"
                  type="number"
                  fullWidth
                  value={editForm.salesRateLimit ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, salesRateLimit: Number(e.target.value) })}
                  helperText="-1 = unlimited"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Offline Transaction Limit"
                  type="number"
                  fullWidth
                  value={editForm.offlineTransactionLimit ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, offlineTransactionLimit: Number(e.target.value) })}
                  helperText="-1 = unlimited"
                  size="small"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Feature Access
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.isVirtualOnly ?? false}
                      onChange={(e) => setEditForm({ ...editForm, isVirtualOnly: e.target.checked })}
                    />
                  }
                  label="Virtual Only Mode"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.hasStorefrontAccess ?? false}
                      onChange={(e) => setEditForm({ ...editForm, hasStorefrontAccess: e.target.checked })}
                    />
                  }
                  label="Storefront Access"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.hasAnalyticsAccess ?? false}
                      onChange={(e) => setEditForm({ ...editForm, hasAnalyticsAccess: e.target.checked })}
                    />
                  }
                  label="Analytics Access"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={updateMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
            }}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
